const OWNED_HEAD_RESOURCE_ATTR = "data-mf-owned";
const TRACKED_HEAD_RESOURCE_SELECTOR =
  'style,link[rel="stylesheet"],script[data-mf-owned]';

const isTrackedHeadResource = (
  node: Node,
): node is HTMLStyleElement | HTMLLinkElement | HTMLScriptElement => {
  if (!(node instanceof HTMLElement)) {
    return false;
  }

  if (node instanceof HTMLStyleElement) {
    return true;
  }

  if (node instanceof HTMLScriptElement) {
    return node.hasAttribute(OWNED_HEAD_RESOURCE_ATTR);
  }

  return (
    node instanceof HTMLLinkElement &&
    node.getAttribute("rel") === "stylesheet"
  );
};

const getTrackedHeadResources = () =>
  Array.from(
    document.head.querySelectorAll<HTMLStyleElement | HTMLLinkElement | HTMLScriptElement>(
      TRACKED_HEAD_RESOURCE_SELECTOR,
    ),
  );

export interface RemoteHeadManager {
  cleanup: () => void;
}

export const createRemoteHeadManager = (
  remoteKey: string,
): RemoteHeadManager => {
  const baselineNodes = new Set(getTrackedHeadResources());
  const ownedNodes = new Set<
    HTMLStyleElement | HTMLLinkElement | HTMLScriptElement
  >();

  const markOwnedResource = (
    node: HTMLStyleElement | HTMLLinkElement | HTMLScriptElement,
  ) => {
    if (baselineNodes.has(node)) {
      return;
    }

    if (
      node instanceof HTMLScriptElement &&
      node.getAttribute(OWNED_HEAD_RESOURCE_ATTR) !== remoteKey
    ) {
      return;
    }

    node.setAttribute(OWNED_HEAD_RESOURCE_ATTR, remoteKey);
    ownedNodes.add(node);
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (isTrackedHeadResource(node)) {
          markOwnedResource(node);
        }
      });
    }
  });

  observer.observe(document.head, {
    childList: true,
  });

  return {
    cleanup: () => {
      observer.disconnect();

      ownedNodes.forEach((node) => {
        if (!node.isConnected || baselineNodes.has(node)) {
          return;
        }

        node.remove();
      });
    },
  };
};

export const remoteHeadManagerSelectors = {
  ownedHeadResourceAttr: OWNED_HEAD_RESOURCE_ATTR,
  trackedHeadResourceSelector: TRACKED_HEAD_RESOURCE_SELECTOR,
} as const;
